import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Package } from "lucide-react";
import { fetchBranding, cachedBranding, brandingLogoUrl } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(cachedBranding() || { app_name: "GudangFlow", has_logo: false });
  useEffect(() => {
    fetchBranding().then((b) => { setBrand(b); document.title = b.app_name; });
  }, []);
  const logoBox = (size, iconSize, dark) => brand.has_logo ? (
    <img src={brandingLogoUrl()} alt={brand.app_name} className={`${size} rounded-xl object-cover`} />
  ) : (
    <div className={`${size} rounded-xl ${dark ? "bg-blue-600" : "bg-slate-900 text-white"} flex items-center justify-center`}><Package size={iconSize} /></div>
  );

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Selamat datang, ${u.full_name}`);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal login");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left: brand panel */}
      <div className="hidden md:flex md:w-1/2 relative bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-60" />
        <div className="relative z-10 p-12 flex flex-col justify-between w-full">
          <div className="flex items-center gap-3">
            {logoBox("w-11 h-11", 22, true)}
            <span className="text-xl font-extrabold tracking-tight" style={{fontFamily:'Manrope'}}>{brand.app_name}</span>
          </div>
          <div>
            <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight" style={{fontFamily:'Manrope'}}>
              Kontrol penuh<br/>gudang pusat <span className="text-blue-400">&</span><br/>cabang kota.
            </h1>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">
              Zero kecurangan. Setiap gerakan barang direkam dengan foto, GPS, dan waktu — dari bahan baku sampai drop-off ke JNT.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3 text-xs uppercase tracking-widest text-slate-400">
              <div className="border-t border-slate-700 pt-3">Foto Bukti</div>
              <div className="border-t border-slate-700 pt-3">GPS Real-time</div>
              <div className="border-t border-slate-700 pt-3">Audit Log</div>
            </div>
          </div>
          <p className="text-xs text-slate-500">© 2026 {brand.app_name}</p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex flex-col p-6 md:p-12">
        <div className="md:hidden flex items-center gap-3 mb-8">
          {logoBox("w-10 h-10", 20, false)}
          <span className="text-lg font-extrabold" style={{fontFamily:'Manrope'}}>{brand.app_name}</span>
        </div>

        <div className="max-w-sm w-full mx-auto md:mx-0 md:mt-24">
          <p className="text-xs uppercase tracking-widest text-blue-600 font-bold mb-3">Masuk Akun</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{fontFamily:'Manrope'}}>Selamat datang kembali</h2>
          <p className="mt-3 text-slate-600">Gunakan akun yang diberikan admin.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Username</label>
              <input
                data-testid="login-username"
                type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                autoFocus required
                className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-base bg-white"
                placeholder="misal: admin"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Password</label>
              <input
                data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full h-12 px-4 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-base bg-white"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              {loading ? "Memproses..." : (<><LogIn size={18} /> Masuk</>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
